import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageBack from '../ui/PageBack';
import { useToast } from '../app/ToastProvider';
import { APPEARANCES, useAppearance } from '../app/appearance';
import { DEFAULT_SETTINGS_SECTION, settingsPath, sourcesPath } from '../app/paths';
import {
  Bell,
  CircleDot,
  ClipboardList,
  Database,
  Eye,
  FileText,
  Plug,
  Radiation,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import NotificationsPage from './NotificationsPage';
import IncidentReports from '../components/c3/IncidentReports';
import YourAccount from '../components/c3/YourAccount';
import OrgSwitcher from '../components/OrgSwitcher';
import { ecsRestart, nukeSystem } from '../api-utils';
import ThreatTestsControls from '../components/ThreatTestsControls';
import type { AwsTestSubhandler } from '../api-utils/tests';

interface SettingsPageProps {
  onBack: () => void;
  section?: string;
  onSection?: (section: string) => void;
  demoMode: boolean;
  setDemoMode: (v: boolean) => void;
  autoRemediate: boolean;
  setAutoRemediate: (v: boolean) => void;
  selectedAwsTests: Set<AwsTestSubhandler>;
  setSelectedAwsTests: React.Dispatch<React.SetStateAction<Set<AwsTestSubhandler>>>;
  selectedCrowdstrikeTests: Set<number>;
  setSelectedCrowdstrikeTests: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedOktaTests: Set<number>;
  setSelectedOktaTests: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedCrossDomainTests: Set<number>;
  setSelectedCrossDomainTests: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedIdsCount: number;
  /** Opens the Agent Rank policy (which kinds of action run by themselves and which wait for a person). */
  onOpenAgentRank: () => void;
  onMonitorTests: () => void;
  onLaunchAws: () => void;
  onLaunchOkta: () => void;
  onComingSoon: (label: string) => void;
  onCloseSelected: () => void;
  onResetAll: () => void;
}

/**
 * Settings is a screen of five boxes of the same size, not a menu (client notes 10/26, Settings 1). Connectors goes to the
 * page where a source is added; the others are sections of this screen.
 */
const HUB: { id: string; title: string; text: string; icon: LucideIcon; to: string }[] = [
  { id: 'connectors', title: 'Connectors', text: 'Connect and manage the sources Arbitium reads.', icon: Plug, to: sourcesPath() },
  { id: 'notifications', title: 'Notifications', text: 'A log of what Arbitium did, and when.', icon: Bell, to: settingsPath('notifications') },
  { id: 'users', title: 'Users and Roles', text: 'Who can see, decide and run what.', icon: Users, to: settingsPath('users') },
  { id: 'reports', title: 'Reports', text: 'Incident Reports, one for each campaign or incident closed.', icon: FileText, to: settingsPath('reports') },
  { id: 'data', title: 'Data', text: 'Demo or live data, organization, test tools and reset.', icon: Database, to: settingsPath('data') },
];

/** The sections of this screen; any other address under /settings shows the boxes. */
const SECTION_TITLES: Record<string, string> = {
  notifications: 'Notifications',
  users: 'Users and Roles',
  reports: 'Reports',
  data: 'Data',
};

const SettingsPage: React.FC<SettingsPageProps> = ({
  onBack,
  section = DEFAULT_SETTINGS_SECTION,
  demoMode,
  setDemoMode,
  autoRemediate,
  setAutoRemediate,
  selectedAwsTests,
  setSelectedAwsTests,
  selectedCrowdstrikeTests,
  setSelectedCrowdstrikeTests,
  selectedOktaTests,
  setSelectedOktaTests,
  selectedCrossDomainTests,
  setSelectedCrossDomainTests,
  selectedIdsCount,
  onOpenAgentRank,
  onMonitorTests,
  onLaunchAws,
  onLaunchOkta,
  onComingSoon,
  onCloseSelected,
  onResetAll,
}) => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const notifyError = (message: string) => notify({ tone: 'error', message });
  const { appearance, setAppearance } = useAppearance();
  const [loading, setLoading] = useState<'ecs' | 'nuke' | null>(null);
  const [nukeSafeWord, setNukeSafeWord] = useState('');
  const [showResetPanel, setShowResetPanel] = useState(false);
  const [resetMode, setResetMode] = useState<'none' | 'ecs' | 'nuke'>('none');

  const current = SECTION_TITLES[section] ? section : DEFAULT_SETTINGS_SECTION;
  const title = SECTION_TITLES[current] ?? 'Settings';
  const isHub = current === DEFAULT_SETTINGS_SECTION;
  const isData = current === 'data';

  return (
    <div className="min-h-view bg-c3-bg font-c3 text-c3-white">
      <header className="relative z-10 border-b border-line px-7 pb-5 pt-4">
        <PageBack onClick={onBack} />
        <h1 className="c3-page-title">{title}</h1>
      </header>

      <main className="relative z-10 px-7 py-6">
        <div className={isHub ? '' : 'max-w-4xl'}>
          {isHub && (
            <ul className="c3-settings-grid" aria-label="Settings">
              {HUB.map(({ id, title: boxTitle, text, icon: Icon, to }) => (
                <li key={id}>
                  <button type="button" className="c3-settings-card" onClick={() => navigate(to)}>
                    <span className="c3-settings-card-icon">
                      <Icon size={20} strokeWidth={1.5} aria-hidden />
                    </span>
                    <span className="c3-settings-card-title">{boxTitle}</span>
                    <span className="c3-settings-card-text">{text}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {isData && (
            <>
          <p className="text-c3-mute mb-6">Demo controls, organization, threat tests, and system reset.</p>

          {/* Demo & Autonomy — relocated from header */}
          <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
            <h2 className="text-[16px] font-semibold mb-1">Demo & Autonomy</h2>
            <p className="text-sm text-c3-mute mb-4">
              Demo shows an invented tenant and Live reads your real data. Auto runs remediations without asking you; Manual asks first.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setDemoMode(!demoMode)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border bg-c3-purple/15 text-c3-white border-c3-purple/50"
              >
                {demoMode ? <ClipboardList className="w-4 h-4" /> : <CircleDot className="w-4 h-4" />}
                {demoMode ? 'Demo' : 'Live'}
              </button>
              <button
                type="button"
                onClick={() => setAutoRemediate(!autoRemediate)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border bg-c3-purple/15 text-c3-white border-c3-purple/50"
              >
                {autoRemediate ? <Zap className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {autoRemediate ? 'Auto' : 'Manual'}
              </button>
            </div>
          </section>

          <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
            <h2 className="text-[16px] font-semibold mb-1">Organization</h2>
            <p className="text-sm text-c3-mute mb-4">
              The workspace the console reads in Live. Changing it reloads the console.
            </p>
            <OrgSwitcher variant="panel" />
          </section>

          <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
            <h2 className="text-[16px] font-semibold mb-1">Appearance</h2>
            <p className="text-sm text-c3-mute mb-4">
              Normal is the dark navy look and the default. Soft is the light one. The choice is kept in this browser.
            </p>
            <div className="flex flex-wrap gap-3" role="group" aria-label="Appearance">
              {APPEARANCES.map(({ id, label, hint }) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={appearance === id}
                  onClick={() => setAppearance(id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border ${
                    appearance === id
                      ? 'bg-c3-purple/15 text-c3-white border-c3-purple/50'
                      : 'bg-transparent text-c3-mute border-c3-panel-bd hover:text-c3-white'
                  }`}
                >
                  {label}
                  <span className="font-normal text-c3-mute">· {hint}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Threat tests — relocated from header */}
          <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
            <h2 className="text-[16px] font-semibold mb-1">Threat tests</h2>
            <p className="text-sm text-c3-mute mb-4">
              Launch AWS/Okta backend test threats, monitor runs, or clear the local feed.
            </p>
            <ThreatTestsControls
              layout="panel"
              selectedAwsTests={selectedAwsTests}
              setSelectedAwsTests={setSelectedAwsTests}
              selectedCrowdstrikeTests={selectedCrowdstrikeTests}
              setSelectedCrowdstrikeTests={setSelectedCrowdstrikeTests}
              selectedOktaTests={selectedOktaTests}
              setSelectedOktaTests={setSelectedOktaTests}
              selectedCrossDomainTests={selectedCrossDomainTests}
              setSelectedCrossDomainTests={setSelectedCrossDomainTests}
              selectedIdsCount={selectedIdsCount}
              onMonitorTests={onMonitorTests}
              onLaunchAws={onLaunchAws}
              onLaunchOkta={onLaunchOkta}
              onComingSoon={onComingSoon}
              onCloseSelected={onCloseSelected}
              onResetAll={onResetAll}
            />
          </section>
            </>
          )}

          {current === 'notifications' && (
            <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
              <NotificationsPage embedded />
            </section>
          )}

          {current === 'users' && (
            <>
              <YourAccount />
              <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
                <h2 className="text-[16px] font-semibold mb-1">Agent Rank policy</h2>
                <p className="text-sm text-c3-mute mb-4">
                  Decide which kinds of action run by themselves (Auto) and which wait for a person (Gated).
                </p>
                <button
                  type="button"
                  onClick={onOpenAgentRank}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border bg-c3-purple/15 text-c3-white border-c3-purple/50"
                >
                  Open Agent Rank policy
                </button>
              </section>
            </>
          )}

          {current === 'reports' && (
            <section className="mb-8 bg-c3-panel border border-c3-panel-bd rounded-xl p-6">
              <h2 className="text-[16px] font-semibold mb-1">Incident Reports</h2>
              <p className="text-sm text-c3-mute mb-4">
                An Incident Report (IR) is created every time a campaign or an incident is closed.
              </p>
              <IncidentReports />
            </section>
          )}

          {isData && (
          <div className="mt-10 border-t border-crit/25 pt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-crit/10 text-crit border border-crit/40">
                <Radiation className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-crit font-semibold text-[16px]">Reset system</h2>
                <p className="text-sm text-c3-mute">
                  Administrative actions for test environments. These operations are destructive and should never be used in production.
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                setShowResetPanel((prev) => !prev);
                setResetMode('none');
                setNukeSafeWord('');
              }}
              className="px-4 py-2 rounded-lg border border-crit/50 text-crit hover:bg-crit/10 transition-colors text-sm font-medium bg-transparent cursor-pointer"
            >
              {showResetPanel ? 'Hide reset options' : 'Show reset options'}
            </button>

            {showResetPanel && (
              <div className="mt-4 space-y-4 bg-c3-panel border border-crit/30 rounded-xl p-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <button
                    onClick={async () => {
                      setResetMode('ecs');
                      setLoading('ecs');
                      try {
                        const res = await ecsRestart();
                        if (res.success) {
                          notify({ tone: 'success', message: 'ECS restart requested successfully.' });
                        } else {
                          const msg =
                            (res.output as any)?.[0]?.error ||
                            (res as any)?.error ||
                            'ECS restart failed.';
                          notifyError(msg);
                        }
                      } catch (e) {
                        notifyError(e instanceof Error ? e.message : String(e));
                      } finally {
                        setLoading(null);
                      }
                    }}
                    disabled={loading !== null}
                    className="flex-1 px-4 py-3 rounded-lg border border-c3-purple/40 bg-c3-tile text-c3-white hover:bg-acc/10 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {loading === 'ecs' ? 'Restarting ECS…' : 'ECS restart only'}
                  </button>

                  <button
                    onClick={() => {
                      setResetMode('nuke');
                      setNukeSafeWord('');
                    }}
                    disabled={loading !== null}
                    className="flex-1 px-4 py-3 rounded-lg border border-crit/50 bg-crit/5 text-crit hover:bg-crit/15 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Complete restart (nuke)
                  </button>
                </div>

                {resetMode === 'nuke' && (
                  <div className="mt-4 space-y-3 border border-crit/30 bg-crit/5 rounded-lg p-4">
                    <p className="text-sm font-semibold text-crit">
                      WARNING this action cannot be reversed.
                    </p>
                    <p className="text-sm text-crit">All data will be deleted!</p>
                    <p className="text-xs text-t2">Type &quot;nuke&quot; to confirm deletion.</p>
                    <input
                      type="text"
                      value={nukeSafeWord}
                      onChange={(e) => setNukeSafeWord(e.target.value)}
                      placeholder="Type nuke to confirm"
                      className="mt-1 w-full rounded-lg bg-c3-bg border border-crit/60 px-3 py-2 text-sm text-t1 placeholder:text-t3 focus:outline-none focus:ring-2 focus:ring-crit/50"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={async () => {
                          setLoading('nuke');
                          try {
                            const res = await nukeSystem(nukeSafeWord);
                            if (res.success) {
                              notify({ tone: 'success', message: 'Complete restart (nuke) requested successfully.' });
                            } else {
                              const msg =
                                (res.output as any)?.[0]?.error ||
                                (res as any)?.error ||
                                'Complete restart failed.';
                              notifyError(msg);
                            }
                          } catch (e) {
                            notifyError(e instanceof Error ? e.message : String(e));
                          } finally {
                            setLoading(null);
                          }
                        }}
                        disabled={loading !== null || nukeSafeWord.length === 0}
                        className="px-4 py-2 rounded-lg bg-crit text-on-fill text-sm font-semibold hover:bg-crit/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer border-none"
                      >
                        {loading === 'nuke' ? 'Requesting nuke…' : 'Confirm nuke restart'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </main>

    </div>
  );
};

export default SettingsPage;
